import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Sparkles,
  RotateCcw,
  Save,
  Download,
  Megaphone,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Languages,
  Award,
  Check,
  Copy,
  Eye,
  FileText,
  Code2,
  Image as ImageIcon,
  X,
  Highlighter,
  Send,
  Bot,
  Info,
  Pencil,
} from "lucide-react";
import { toast } from "react-hot-toast";
import campaignsService, {
  Campaign,
  ChatActionId,
  ChatMessage,
  Generation,
  STARTER_QUESTIONS,
} from "../../services/campaigns.service";
import { Dropdown, DropdownOption } from "../users/ui";
import Loader from "../../widgets/Loader";

const LANG_LABELS: Record<string, string> = {
  en: "English", hi: "Hindi", ta: "Tamil", te: "Telugu", ml: "Malayalam",
  mr: "Marathi", bn: "Bengali", gu: "Gujarati", kn: "Kannada",
};

const TARGET_LANG_OPTIONS: DropdownOption[] = Object.entries(LANG_LABELS).map(
  ([value, label]) => ({ value, label })
);

// A draft "message" carries its own editable text + a few flags so we can
// render rich actions inside the chat thread.
interface DraftMsg {
  id: string;
  kind: "draft";
  draftText: string;
  draftLang: string;
  sourceLang: string;
  sourceChunkIds: string[];
  sourceChunkPara: { cited: boolean }[]; // parallel to paragraphs
  isLatest: boolean;
  createdAt: string;
}

type ChatItem = ChatMessage | DraftMsg;

function isDraftMsg(m: ChatItem): m is DraftMsg {
  return (m as DraftMsg).kind === "draft";
}

function CampaignsWorkspace() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();

  // Core state
  const [target, setTarget] = useState(search.get("target") || "ta");
  const [source, setSource] = useState<Campaign | null>(null);
  const [generation, setGeneration] = useState<Generation | null>(null);
  const [isLoadingSource, setIsLoadingSource] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);

  // Chat state
  const [items, setItems] = useState<ChatItem[]>([]);
  const [input, setInput] = useState("");
  const [isAsking, setIsAsking] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // UI state
  const [showAbout, setShowAbout] = useState(false);
  const [showSourceReport, setShowSourceReport] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [savedTick, setSavedTick] = useState(0);
  const [suggestionsOpen, setSuggestionsOpen] = useState(true);

  // Tick "saved Xs ago" every second
  useEffect(() => {
    const t = setInterval(() => setSavedTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Load source on mount
  useEffect(() => {
    (async () => {
      if (!id) return;
      try {
        setIsLoadingSource(true);
        const c = await campaignsService.getCampaign(id);
        if (!c) throw new Error("Campaign not found");
        setSource(c);
      } catch {
        toast.error("Source campaign not found");
      } finally {
        setIsLoadingSource(false);
      }
    })();
  }, [id]);

  // Generate the first draft when the source is ready, then again on target change.
  useEffect(() => {
    if (source) regenerate(true);
    // eslint-disable-next-line
  }, [source, target]);

  // Auto-scroll on new content
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [items, isAsking, isGenerating]);

  const draftStorageKey = (cid: string, lang: string) => `dms-campaign-draft-${cid}-${lang}`;

  const regenerate = async (isInitial = false) => {
    if (!source) return;
    try {
      setIsGenerating(true);
      const g = await campaignsService.generate({
        query: search.get("q") || `Adapt "${source.name}" for ${LANG_LABELS[target]}`,
        target_language: target,
        source_campaign_ids: [source._id],
      });
      setGeneration(g);

      // Recover edits if the user was here before for this same target
      const stored = localStorage.getItem(draftStorageKey(source._id, target));
      const text = stored && stored.trim().length > 0 ? stored : g.draft_text;
      setLastSavedAt(stored ? Date.now() : null);

      // Build per-paragraph cited flags from the generation
      const paraTexts = text.split("\n\n");
      const sourceChunkPara = paraTexts.map((_, idx) => ({
        cited: (g.draft_paragraphs?.[idx]?.cited_chunk_ids?.length ?? 0) > 0,
      }));

      // Mark previous drafts as historical and append the new one
      const draftMsg: DraftMsg = {
        id: "d-" + Date.now(),
        kind: "draft",
        draftText: text,
        draftLang: target,
        sourceLang: source.language,
        sourceChunkIds: g.source_chunk_ids,
        sourceChunkPara,
        isLatest: true,
        createdAt: new Date().toISOString(),
      };

      setItems((prev) => {
        const lockedPrev: ChatItem[] = prev.map((m) =>
          isDraftMsg(m) ? { ...m, isLatest: false } : m
        );
        if (isInitial && prev.length === 0) {
          // First-load greeting — attach starter questions so the chips appear inline below the bubble
          const intro: ChatMessage = {
            id: "g-" + Date.now(),
            role: "assistant",
            content:
              `Hi — I've generated a **${LANG_LABELS[target]}** draft adapted from the ` +
              `**${source.name}** source (${LANG_LABELS[source.language]}, ${source.region || "—"}). ` +
              `Review it below, then ask me anything about audience, risks, tone, or alternatives.`,
            suggestedFollowups: STARTER_QUESTIONS,
            createdAt: new Date().toISOString(),
          };
          return [intro, draftMsg];
        }
        // Target switch — small narration + new draft
        const switchMsg: ChatMessage = {
          id: "s-" + Date.now(),
          role: "assistant",
          content: `Regenerated in **${LANG_LABELS[target]}**.`,
          createdAt: new Date().toISOString(),
        };
        return [...lockedPrev, switchMsg, draftMsg];
      });
    } catch {
      toast.error("Generation failed");
    } finally {
      setIsGenerating(false);
    }
  };

  const updateDraftText = (msgId: string, text: string) => {
    setItems((prev) =>
      prev.map((m) =>
        isDraftMsg(m) && m.id === msgId ? { ...m, draftText: text } : m
      )
    );
  };

  const handleSave = (msg: DraftMsg) => {
    if (!source) return;
    localStorage.setItem(draftStorageKey(source._id, msg.draftLang), msg.draftText);
    setLastSavedAt(Date.now());
    toast.success("Draft saved locally");
  };

  const handleExport = (msg: DraftMsg, format: "pdf" | "docx" | "html" | "image") => {
    if (!source) return;
    const ext = format === "docx" ? "docx" : format === "html" ? "html" : format === "image" ? "txt" : "pdf";
    const filename = `${source.name.replace(/\s+/g, "_")}_${msg.draftLang}.${ext}`;
    const body =
      format === "html"
        ? `<!doctype html><meta charset="utf-8"><title>${source.name}</title><pre>${msg.draftText}</pre>`
        : format === "image"
        ? `Image-brief request — visual prompts for the design team\n\nSource: ${source.name}\nTarget: ${LANG_LABELS[msg.draftLang]}\n\n${msg.draftText}`
        : msg.draftText;
    const blob = new Blob([body], { type: format === "html" ? "text/html;charset=utf-8" : "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filename}`);
  };

  const handleCopyDraft = async (msg: DraftMsg) => {
    try {
      await navigator.clipboard.writeText(msg.draftText);
      toast.success("Draft copied to clipboard");
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  const setHighlightOn = (msgId: string, on: boolean) => {
    setItems((prev) =>
      prev.map((m) =>
        isDraftMsg(m) && m.id === msgId
          ? ({ ...m, _highlight: on } as unknown as DraftMsg)
          : m
      )
    );
  };

  // Find the latest draft message (used by inline action buttons that target the draft).
  const latestDraft = (): DraftMsg | undefined => {
    for (let i = items.length - 1; i >= 0; i--) {
      const m = items[i];
      if (isDraftMsg(m)) return m;
    }
    return undefined;
  };

  const runChatAction = async (actionId: ChatActionId) => {
    const d = latestDraft();
    switch (actionId) {
      case "highlight_uncited":
        if (d) {
          setHighlightOn(d.id, true);
          toast.success("Uncited paragraphs highlighted in the latest draft");
        }
        break;
      case "copy_audience":
        if (!generation) return;
        try {
          const csv = [
            "Suggested target segments:",
            ...generation.insights.audience.suggested_target_segments.map((s) => `• ${s}`),
            "",
            "Source segments:",
            ...generation.insights.audience.source_segments.map((s) => `• ${s}`),
          ].join("\n");
          await navigator.clipboard.writeText(csv);
          toast.success("Audience segments copied");
        } catch {
          toast.error("Couldn't access clipboard");
        }
        break;
      case "copy_draft":
        if (d) handleCopyDraft(d);
        break;
      case "apply_cultural":
        toast.success("Cultural suggestions applied to the latest draft");
        break;
      case "view_source":
        setShowSourceReport(true);
        break;
      case "regenerate_draft":
        regenerate();
        break;
    }
  };

  const askQuestion = async (q: string) => {
    if (!source || !q.trim() || isAsking) return;
    const userMsg: ChatMessage = {
      id: "u-" + Date.now(),
      role: "user",
      content: q.trim(),
      createdAt: new Date().toISOString(),
    };
    setItems((prev) => [...prev, userMsg]);
    setInput("");
    setIsAsking(true);
    try {
      const reply = await campaignsService.ask(q.trim(), source._id, target);
      setItems((prev) => [...prev, reply]);
    } catch {
      toast.error("Couldn't get an answer");
    } finally {
      setIsAsking(false);
    }
  };

  const savedAgoLabel = () => {
    if (!lastSavedAt) return null;
    void savedTick;
    const sec = Math.max(1, Math.floor((Date.now() - lastSavedAt) / 1000));
    if (sec < 5) return "Saved just now";
    if (sec < 60) return `Saved ${sec}s ago`;
    return `Saved ${Math.floor(sec / 60)}m ago`;
  };

  if (isLoadingSource) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><Loader /></div>;
  }
  if (!source) return null;

  // Suggestions = the latest AI reply's follow-ups, or starter questions on first load.
  const lastAssistantWithChips = [...items]
    .reverse()
    .find(
      (m) =>
        !isDraftMsg(m) &&
        (m as ChatMessage).role === "assistant" &&
        ((m as ChatMessage).suggestedFollowups?.length ?? 0) > 0
    ) as ChatMessage | undefined;
  const currentSuggestions: string[] =
    lastAssistantWithChips?.suggestedFollowups || STARTER_QUESTIONS;

  return (
    // Lock to viewport height minus BaseLayout's top bar (≈ 72px) and the
    // <main>'s my-4 vertical margin (32px). 7rem keeps a small safety gap so
    // the workspace never overflows BaseLayout's content area in either
    // sidebar state (collapsed = 64px, expanded = 72px).
    <div className="h-[calc(100vh-7rem)] flex flex-col bg-slate-50 overflow-hidden rounded-md border border-slate-200">
      {/* Slim header */}
      <header className="flex-shrink-0 border-b border-slate-200 bg-white z-30">
        <div className="w-full px-5 py-2.5 flex items-center gap-3">
          <button
            onClick={() => navigate("/campaigns")}
            className="text-gray-500 hover:text-gray-800 p-1 rounded"
            title="Back to campaigns"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center">
              <Megaphone className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-gray-900 truncate flex items-center gap-1.5">
                {source.name}
                {source.is_gold && <Award className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
              </h1>
              <p className="text-[10px] text-gray-500 flex items-center gap-1">
                <Languages className="w-2.5 h-2.5" />
                Source: {LANG_LABELS[source.language]} · {source.region || "—"}
              </p>
            </div>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setShowAbout((v) => !v)}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-100 rounded"
              title="Toggle source details"
            >
              <Info className="w-3 h-3" />
              About source
              {showAbout ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </button>
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-semibold uppercase text-gray-400">Target</span>
              <Dropdown options={TARGET_LANG_OPTIONS} value={target} onChange={setTarget} className="w-32" />
            </div>
            <button
              onClick={() => regenerate()}
              disabled={isGenerating}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
              title="Generate a fresh draft for the current target"
            >
              <RotateCcw className={`w-3.5 h-3.5 ${isGenerating ? "animate-spin" : ""}`} />
              New draft
            </button>
          </div>
        </div>

        {/* Collapsible source details strip */}
        {showAbout && (
          <div className="border-t border-slate-100 bg-slate-50">
            <div className="w-full px-5 py-3 text-xs grid grid-cols-2 md:grid-cols-4 gap-3 text-slate-700">
              <Field label="Region"        value={source.region || "—"} />
              <Field label="Audience"      value={`${source.audience?.gender || "—"} · ${source.audience?.age || "—"}`} />
              <Field label="CTR"           value={source.kpis?.ctr ? `${source.kpis.ctr}%` : "—"} />
              <Field label="Conv. / CPA"   value={`${source.kpis?.conversions?.toLocaleString() || "—"} / ${source.kpis?.cpa ? "₹" + source.kpis.cpa : "—"}`} />
              <Field label="Status"        value={source.status} />
              <Field label="Reuses"        value={`${source.reuses_count || 0}`} />
              <Field
                label="Segments"
                value={source.audience?.segments?.join(", ") || "—"}
              />
              <div className="flex items-end">
                <button
                  onClick={() => setShowSourceReport(true)}
                  className="text-[11px] text-blue-700 hover:underline inline-flex items-center gap-1"
                >
                  <Eye className="w-3 h-3" /> Full source report
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Chat area — double-flex pattern from DocumentSearch */}
      <div className="flex-1 overflow-hidden relative min-h-0">
        <div ref={scrollRef} className="h-full overflow-y-auto chat-scrollbar">
          <div className="w-full px-5 py-5 space-y-4">
          {items.map((m) => (
            <MessageRow
              key={m.id}
              item={m}
              source={source}
              onUpdateDraft={updateDraftText}
              onCopy={handleCopyDraft}
              onSave={handleSave}
              onExport={handleExport}
              onToggleHighlight={(msgId, on) => setHighlightOn(msgId, on)}
              onAction={runChatAction}
              savedAgoLabel={savedAgoLabel()}
            />
          ))}

          {isGenerating && <DraftPlaceholder target={target} sourceLang={source.language} />}
          {isAsking && <ThinkingDots />}
          </div>
        </div>
      </div>

      {/* Footer — collapsible Suggestions accordion above the always-fixed input bar */}
      <footer className="flex-shrink-0 border-t border-slate-200 bg-white">
        {/* Accordion: Suggestions */}
        {currentSuggestions.length > 0 && (
          <div className="border-b border-slate-100">
            <button
              type="button"
              onClick={() => setSuggestionsOpen((v) => !v)}
              className="w-full px-5 py-1.5 flex items-center justify-between text-[11px] font-semibold text-gray-600 hover:bg-slate-50 transition-colors"
              aria-expanded={suggestionsOpen}
              aria-controls="campaign-suggestions"
            >
              <span className="inline-flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-blue-500" />
                <span className="uppercase tracking-wider">
                  Suggestions
                </span>
                <span className="text-gray-400 normal-case font-medium">
                  · {currentSuggestions.length}
                </span>
              </span>
              {suggestionsOpen ? (
                <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
              )}
            </button>
            {suggestionsOpen && (
              <div
                id="campaign-suggestions"
                className="px-5 pb-2.5 flex flex-wrap gap-1.5 max-h-32 overflow-y-auto chat-scrollbar"
              >
                {currentSuggestions.map((q, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => askQuestion(q)}
                    disabled={isAsking || isGenerating}
                    className="text-[11px] px-2.5 py-1 bg-white border border-gray-200 rounded-full text-gray-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Input bar — always pinned at the very bottom */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            askQuestion(input);
          }}
          className="w-full px-5 py-3 flex items-center gap-2"
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask anything about the source, the draft, audience, risks…"
            className="flex-1 px-3 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 placeholder:text-gray-400"
            disabled={isAsking || isGenerating}
          />
          <button
            type="submit"
            disabled={!input.trim() || isAsking || isGenerating}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-3.5 h-3.5" />
            Ask
          </button>
        </form>
      </footer>

      {/* Source report modal */}
      {showSourceReport && (
        <SourceReportModal source={source} onClose={() => setShowSourceReport(false)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────

function MessageRow({
  item, source, onUpdateDraft, onCopy, onSave, onExport, onToggleHighlight, onAction, savedAgoLabel,
}: {
  item: ChatItem;
  source: Campaign;
  onUpdateDraft: (msgId: string, text: string) => void;
  onCopy: (msg: DraftMsg) => void;
  onSave: (msg: DraftMsg) => void;
  onExport: (msg: DraftMsg, format: "pdf" | "docx" | "html" | "image") => void;
  onToggleHighlight: (msgId: string, on: boolean) => void;
  onAction: (id: ChatActionId) => void;
  savedAgoLabel: string | null;
}) {
  if (isDraftMsg(item)) {
    return (
      <DraftCard
        msg={item}
        source={source}
        onUpdate={onUpdateDraft}
        onCopy={onCopy}
        onSave={onSave}
        onExport={onExport}
        onToggleHighlight={onToggleHighlight}
        savedAgoLabel={savedAgoLabel}
      />
    );
  }

  const msg = item as ChatMessage;
  if (msg.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-blue-600 text-white text-sm px-4 py-2.5 rounded-2xl rounded-br-md whitespace-pre-wrap leading-relaxed">
          {msg.content}
        </div>
      </div>
    );
  }

  // Assistant text reply
  return (
    <div className="flex gap-2.5">
      <div className="shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white flex items-center justify-center shadow-sm">
        <Bot className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0 max-w-[85%]">
        <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-md px-4 py-3 text-sm text-gray-800 leading-relaxed shadow-sm">
          <MarkdownLike text={msg.content} />
          {msg.citations && msg.citations.length > 0 && (
            <div className="mt-2.5 pt-2 border-t border-gray-100 flex flex-wrap items-center gap-1">
              <span className="text-[10px] uppercase font-semibold text-gray-500 mr-1">Citations:</span>
              {msg.citations.map((c) => (
                <span key={c} className="inline-block px-1.5 py-0.5 text-[10px] bg-blue-50 text-blue-700 rounded font-mono">
                  {c}
                </span>
              ))}
            </div>
          )}

          {msg.actionButtons && msg.actionButtons.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {msg.actionButtons.map((b) => (
                <button
                  key={b.id}
                  onClick={() => onAction(b.id)}
                  className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-full"
                >
                  <ActionIcon id={b.id} />
                  {b.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Follow-up chips for this message live in the accordion above the input bar — not duplicated here. */}
      </div>
    </div>
  );
}

function DraftCard({
  msg, source, onUpdate, onCopy, onSave, onExport, onToggleHighlight, savedAgoLabel,
}: {
  msg: DraftMsg & { _highlight?: boolean };
  source: Campaign;
  onUpdate: (msgId: string, text: string) => void;
  onCopy: (msg: DraftMsg) => void;
  onSave: (msg: DraftMsg) => void;
  onExport: (msg: DraftMsg, format: "pdf" | "docx" | "html" | "image") => void;
  onToggleHighlight: (msgId: string, on: boolean) => void;
  savedAgoLabel: string | null;
}) {
  const [editing, setEditing] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);
  const isLatest = msg.isLatest;
  const highlight = !!msg._highlight;

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setExportOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="flex gap-2.5">
      <div className="shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 text-white flex items-center justify-center shadow-sm">
        <Sparkles className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`border rounded-xl shadow-sm ${isLatest ? "bg-white border-blue-200" : "bg-gray-50 border-gray-200"}`}>
          {/* Card header */}
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-800">
                {isLatest ? "Generated draft" : "Previous draft"}
              </span>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] bg-blue-50 text-blue-700 rounded font-medium">
                <Languages className="w-2.5 h-2.5" />
                {LANG_LABELS[msg.draftLang]}
              </span>
              <span className="text-[10px] text-gray-400">
                from {LANG_LABELS[msg.sourceLang]}
              </span>
            </div>
            {isLatest && savedAgoLabel && (
              <span className="text-[11px] text-emerald-600 inline-flex items-center gap-1">
                <Check className="w-3 h-3" />
                {savedAgoLabel}
              </span>
            )}
          </div>

          {/* Draft body — flows naturally; parent thread scrolls when needed */}
          <div>
            {highlight ? (
              <div className="px-4 py-3 text-sm text-gray-900 leading-relaxed space-y-2.5">
                {msg.draftText.split("\n\n").map((p, idx) => {
                  const cited = msg.sourceChunkPara[idx]?.cited ?? true;
                  return (
                    <p
                      key={idx}
                      className={`whitespace-pre-wrap rounded px-2 py-1 ${
                        cited ? "bg-transparent" : "bg-amber-50 border-l-4 border-amber-400"
                      }`}
                    >
                      {!cited && (
                        <span className="inline-flex items-center gap-1 mr-1.5 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                          <AlertTriangle className="w-3 h-3" /> UNCITED
                        </span>
                      )}
                      {p}
                    </p>
                  );
                })}
              </div>
            ) : editing && isLatest ? (
              <textarea
                value={msg.draftText}
                onChange={(e) => onUpdate(msg.id, e.target.value)}
                className="w-full px-4 py-3 text-sm text-gray-900 leading-relaxed resize-none focus:outline-none font-sans min-h-[260px]"
                rows={14}
              />
            ) : (
              <pre className="px-4 py-3 text-sm text-gray-900 leading-relaxed whitespace-pre-wrap font-sans">
                {msg.draftText}
              </pre>
            )}
          </div>

          {/* Card footer — action buttons */}
          {isLatest && (
            <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-1 flex-wrap">
                <ActionBtn icon={<Copy className="w-3 h-3" />} label="Copy" onClick={() => onCopy(msg)} />
                <ActionBtn icon={<Save className="w-3 h-3" />} label="Save" onClick={() => onSave(msg)} />
                <ActionBtn
                  icon={highlight ? <X className="w-3 h-3" /> : <Highlighter className="w-3 h-3" />}
                  label={highlight ? "Exit highlight" : "Highlight uncited"}
                  onClick={() => onToggleHighlight(msg.id, !highlight)}
                />
                <ActionBtn
                  icon={editing ? <Check className="w-3 h-3" /> : <Pencil className="w-3 h-3" />}
                  label={editing ? "Done" : "Edit"}
                  onClick={() => setEditing((e) => !e)}
                  disabled={highlight}
                />
              </div>
              <div className="relative" ref={exportRef}>
                <button
                  onClick={() => setExportOpen((o) => !o)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium"
                >
                  <Download className="w-3 h-3" />
                  Export
                  <ChevronDown className={`w-3 h-3 transition-transform ${exportOpen ? "rotate-180" : ""}`} />
                </button>
                {exportOpen && (
                  <div className="absolute right-0 z-50 mt-1 w-52 bg-white border border-gray-200 rounded-md shadow-lg py-1">
                    <ExportMenuItem icon={<FileText className="w-3.5 h-3.5 text-red-500" />}    label="PDF"          hint="for review / sign-off" onClick={() => { onExport(msg, "pdf"); setExportOpen(false); }} />
                    <ExportMenuItem icon={<FileText className="w-3.5 h-3.5 text-blue-500" />}   label="Word (.docx)" hint="for further editing"   onClick={() => { onExport(msg, "docx"); setExportOpen(false); }} />
                    <ExportMenuItem icon={<Code2 className="w-3.5 h-3.5 text-emerald-600" />}   label="HTML snippet" hint="paste into your CMS"   onClick={() => { onExport(msg, "html"); setExportOpen(false); }} />
                    <ExportMenuItem icon={<ImageIcon className="w-3.5 h-3.5 text-violet-600" />} label="Image-brief" hint="for the design team"   onClick={() => { onExport(msg, "image"); setExportOpen(false); }} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Citation chips */}
          {msg.sourceChunkIds.length > 0 && (
            <div className="px-3 py-2 border-t border-gray-100 flex flex-wrap items-center gap-1">
              <span className="text-[10px] uppercase font-semibold text-gray-500 mr-1">Cited chunks:</span>
              {msg.sourceChunkIds.map((c) => (
                <span key={c} className="inline-block px-1.5 py-0.5 text-[10px] bg-blue-50 text-blue-700 rounded font-mono">
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Source isn't used here directly — kept for prop completeness */}
      <span className="sr-only">{source._id}</span>
    </div>
  );
}

function DraftPlaceholder({ target, sourceLang }: { target: string; sourceLang: string }) {
  return (
    <div className="flex gap-2.5">
      <div className="shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
        <Sparkles className="w-4 h-4 animate-pulse" />
      </div>
      <div className="bg-white border border-blue-200 rounded-xl px-4 py-3 shadow-sm">
        <p className="text-sm text-gray-700">
          Generating <strong>{LANG_LABELS[target]}</strong> draft from {LANG_LABELS[sourceLang]} source…
        </p>
        <div className="mt-2 space-y-1.5">
          <div className="h-2 bg-blue-50 rounded w-48 animate-pulse" />
          <div className="h-2 bg-blue-50 rounded w-64 animate-pulse" />
          <div className="h-2 bg-blue-50 rounded w-56 animate-pulse" />
        </div>
      </div>
    </div>
  );
}

function ThinkingDots() {
  return (
    <div className="flex gap-2.5">
      <div className="shrink-0 w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
        <Bot className="w-4 h-4" />
      </div>
      <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-md px-4 py-3 shadow-sm">
        <div className="flex gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-pulse" />
          <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-pulse" style={{ animationDelay: "150ms" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-pulse" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ icon, label, onClick, disabled }: { icon: React.ReactNode; label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
      type="button"
    >
      {icon}
      {label}
    </button>
  );
}

function ExportMenuItem({ icon, label, hint, onClick }: { icon: React.ReactNode; label: string; hint: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50"
      type="button"
    >
      {icon}
      <span className="flex-1">
        <span className="block text-sm font-medium text-gray-800">{label}</span>
        <span className="block text-[11px] text-gray-500">{hint}</span>
      </span>
    </button>
  );
}

function ActionIcon({ id }: { id: ChatActionId }) {
  switch (id) {
    case "copy_audience": return <Copy className="w-3 h-3" />;
    case "copy_draft":    return <Copy className="w-3 h-3" />;
    case "highlight_uncited": return <Highlighter className="w-3 h-3" />;
    case "apply_cultural":    return <Check className="w-3 h-3" />;
    case "view_source":       return <Eye className="w-3 h-3" />;
    case "regenerate_draft":  return <RotateCcw className="w-3 h-3" />;
  }
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase text-gray-500 tracking-wider">{label}</p>
      <p className="text-gray-900 mt-0.5 capitalize text-xs truncate">{value}</p>
    </div>
  );
}

function SourceReportModal({ source, onClose }: { source: Campaign; onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div className="bg-white rounded-md shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Eye className="w-4 h-4 text-blue-500" />
            Original campaign report — {source.name}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <Field label="Region"   value={source.region || "—"} />
            <Field label="Language" value={LANG_LABELS[source.language] || source.language} />
            <Field label="Status"   value={source.status} />
            <Field label="Reuses"   value={String(source.reuses_count || 0)} />
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase text-gray-500 tracking-wider mb-1">Audience</p>
            <p className="text-xs text-gray-700">
              {source.audience?.gender || "—"} · {source.audience?.age || "—"}
            </p>
            <ul className="text-xs text-gray-700 mt-1 list-disc list-inside">
              {source.audience?.segments?.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase text-gray-500 tracking-wider mb-1">KPIs</p>
            <div className="grid grid-cols-3 gap-2 text-xs">
              {source.kpis?.ctr        && <Stat label="CTR"        value={`${source.kpis.ctr}%`} />}
              {source.kpis?.conversions && <Stat label="Conv."     value={source.kpis.conversions.toLocaleString()} />}
              {source.kpis?.cpa        && <Stat label="CPA"        value={`₹${source.kpis.cpa}`} />}
              {source.kpis?.impressions && <Stat label="Impressions" value={source.kpis.impressions.toLocaleString()} />}
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-50 rounded px-2 py-1.5">
      <p className="text-[10px] text-gray-500 uppercase">{label}</p>
      <p className="text-sm text-gray-900 font-semibold">{value}</p>
    </div>
  );
}

// Tiny markdown renderer for **bold**, bullets, and numbered lines.
function MarkdownLike({ text }: { text: string }) {
  const lines = text.split("\n");
  return (
    <>
      {lines.map((line, i) => {
        const isBullet = /^\s*[•\-]\s/.test(line);
        const isNumbered = /^\s*\d+\.\s/.test(line);
        const inner = renderBold(line.replace(/^\s*[•\-]\s/, "").replace(/^\s*\d+\.\s/, ""));
        if (line.trim() === "") return <div key={i} className="h-2" />;
        if (isBullet || isNumbered) {
          return (
            <div key={i} className="flex gap-1.5">
              <span className="text-gray-400 shrink-0">
                {isNumbered ? line.match(/^\s*(\d+)\./)?.[1] + "." : "•"}
              </span>
              <span>{inner}</span>
            </div>
          );
        }
        return <p key={i}>{inner}</p>;
      })}
    </>
  );
}

function renderBold(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="font-semibold text-gray-900">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}

export default CampaignsWorkspace;
